import * as React from 'react';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '../lib/cn';

const alertVariants = cva(
  [
    'relative w-full rounded-[var(--alert-radius)] py-2 pl-2 pr-4 text-sm',
    '[&>svg+div]:translate-y-[-2px]',
    '[&>svg]:absolute [&>svg]:left-2 [&>svg]:text-[currentColor]',
    '[&>svg~*]:pl-6',
  ],
  {
    variants: {
      variant: {
        default:
          'bg-[color-mix(in_hsl,var(--alert-default-text)_10%,transparent)] text-[var(--alert-default-text)]',
        destructive:
          'text-[var(--alert-destructive-text)] [&>svg]:text-[var(--alert-destructive-text)]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({className, variant, ...props}, ref) => (
  <div
    ref={ref}
    role="alert"
    data-slot="alert"
    className={cn(alertVariants({variant}), className)}
    {...props}
  />
));
Alert.displayName = 'Alert';

export const AlertTitle = React.forwardRef<
  React.ElementRef<'h5'>,
  React.ComponentPropsWithoutRef<'h5'>
>(({className, ...props}, ref) => (
  <h5
    ref={ref}
    data-slot="alert-title"
    className={cn('mb-1 font-medium leading-none tracking-tight', className)}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

export const AlertDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({className, ...props}, ref) => (
  <div
    ref={ref}
    data-slot="alert-description"
    className={cn('text-sm [&_p]:leading-relaxed', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';
